import { Fragment } from "react";
import { Helmet } from "react-helmet-async";
import { HiOutlineShieldCheck } from "react-icons/hi2";

import classes from "./LegalPage.module.css";
import { bankName, email } from "../../util/config";

const PrivacyPolicy = () => {
  return (
    <Fragment>
      <Helmet>
        <title>Privacy Policy — {bankName}</title>
        <meta
          name="description"
          content={`How ${bankName} collects, uses, and protects your personal data.`}
        />
        <link rel="canonical" href="/privacy" />
      </Helmet>
      <div className={classes.legal}>
        <header className={classes.legalHeader}>
          <h1 className={classes.legalTitle}>
            <HiOutlineShieldCheck className={classes.legalTitleIcon} aria-hidden />
            Privacy Policy
          </h1>
          <p className={classes.lastUpdated}>Last updated: 12 April 2026</p>
        </header>
        <article className={classes.article}>
          <h2>Who we are</h2>
          <p>
            This policy describes how {bankName} and its group companies (“we”,
            “us”) process personal data when you use our websites, mobile
            applications, and banking and insurance services. Our registered
            contact for data protection queries is: {email}.
          </p>

          <h2>What we collect</h2>
          <p>We may process:</p>
          <ul>
            <li>
              Identity and contact details (name, address, date of birth,
              telephone, email).
            </li>
            <li>
              Financial information (account numbers, transaction data, credit
              history where permitted).
            </li>
            <li>
              Technical data (IP address, device identifiers, cookies, and
              similar technologies).
            </li>
            <li>
              Communications you send us (including call recordings where
              lawfully used and notified).
            </li>
          </ul>

          <h2>How we use your data</h2>
          <p>We process personal data to:</p>
          <ul>
            <li>Provide accounts, payments, lending, and insurance services.</li>
            <li>
              Meet legal and regulatory obligations (including anti-money
              laundering and fraud prevention).
            </li>
            <li>Improve our services, security, and customer experience.</li>
            <li>
              Send service messages and, where you have consented or where
              allowed, marketing you can opt out of at any time.
            </li>
          </ul>

          <h2>Legal bases</h2>
          <p>
            Depending on the activity, we rely on performance of a contract,
            legal obligation, legitimate interests (balanced against your
            rights), or consent—particularly for certain cookies and direct
            marketing where required.
          </p>

          <h2>Sharing and international transfers</h2>
          <p>
            We may share data with regulators, credit reference agencies,
            fraud-prevention bodies, insurers, and carefully vetted service
            providers who process data on our instructions. If data is
            transferred outside the UK or EEA, we ensure appropriate safeguards
            (such as standard contractual clauses) are in place.
          </p>

          <h2>Retention</h2>
          <p>
            We keep information only as long as needed for the purposes above
            and to comply with law—including retention periods set by financial
            services regulations.
          </p>

          <h2>Your rights</h2>
          <p>
            Subject to applicable law, you may have rights to access, rectify,
            erase, restrict, or object to certain processing, and to data
            portability. You may withdraw consent where processing is
            consent-based. You may lodge a complaint with the Information
            Commissioner’s Office (ICO) in the UK.
          </p>

          <h2>Cookies</h2>
          <p>
            We use cookies and similar technologies to operate the site,
            remember preferences, analyse traffic, and support security. You can
            manage non-essential cookies through our cookie tool where
            available, or your browser settings.
          </p>

          <div className={classes.contactBox}>
            <p>
              <strong>Contact:</strong> For privacy requests, email{" "}
              <a href={`mailto:${email}`}>{email}</a> with “Privacy Request” in
              the subject line. We will respond within the timeframe required by
              law.
            </p>
          </div>
        </article>
      </div>
    </Fragment>
  );
};

export default PrivacyPolicy;
